import React from "react";
import DatePicker from "react-datepicker";
import MultiSelectDropdown from "../MultiSelectDropdown";

const PreviewFieldRenderer = ({ field, fontStyles, modalSettingsObj }) => {
  const getCharacterLimit = () => {
    return (
      field.characterLimit || modalSettingsObj?.characterLimit || undefined
    );
  };

  const normalizeFieldType = (fieldType) => {
    if (!fieldType) return "text";

    switch (fieldType.toLowerCase()) {
      case "text":
      case "text input":
      case "text-input":
        return "text";
      case "textarea":
      case "text mulit line input":
      case "text-mulitline":
        return "textarea";
      case "date":
        return "date";
      case "select":
      case "dropdown":
        return "select";
      case "checkbox":
        return "checkbox";
      case "radio":
        return "radio";
      case "initials":
      case "initial":
        return "initials";
      case "signature":
        return "signature";
      default:
        return fieldType.toLowerCase();
    }
  };

  const renderFieldContent = () => {
    const fieldType = normalizeFieldType(field.type || field.inputType);

    switch (fieldType) {
      case "text":
        return (
          <input
            type="text"
            placeholder={field.value || field.placeholder}
            value=""
            className="field-input"
            id={`field-${field.id}`}
            name={`field-${field.id}`}
            style={fontStyles}
            maxLength={getCharacterLimit()}
            disabled
            readOnly
          />
        );

      case "textarea":
        return (
          <textarea
            placeholder={field.value || field.placeholder}
            value=""
            className="field-textarea"
            id={`field-${field.id}`}
            name={`field-${field.id}`}
            style={fontStyles}
            maxLength={getCharacterLimit()}
            disabled
            readOnly
          />
        );

      case "date":
        return (
          <>
            <DatePicker
              selected={field.value ? new Date(field.value) : null}
              placeholderText={
                field.placeholder || field.value || "Select date"
              }
              className="field-date"
              style={fontStyles}
              disabled
              dateFormat="MM-dd-yyyy"
              dropdownMode="select"
              showMonthDropdown
              showYearDropdown
              maxDate={new Date()}
              openToDate={field.value ? new Date(field.value) : null}
            />
            <span
              className="calendar-icon"
              onClick={() => {
                const dateInput = document.querySelector(
                  `[data-field-id="${field.id}"] .field-date`,
                );
                if (dateInput) {
                  dateInput.focus();
                  dateInput.click();
                }
              }}
            ></span>
          </>
        );

      case "select":
        return field.multiple_options ? (
          <MultiSelectDropdown
            options={field.options || []}
            value={field.value || []}
            placeholder="Select an option"
            style={fontStyles}
            disabled
          />
        ) : (
          <select
            className="field-select"
            style={fontStyles}
            value={field.value || ""}
            disabled
          >
            <option value="">Select an option</option>
            {field.options?.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "checkbox":
        return (
          <input
            type="checkbox"
            className="field-checkbox"
            checked={field.value === "true" || field.value === true}
            disabled
            readOnly
          />
        );

      case "radio":
        return <input type="radio" className="field-radio" disabled readOnly />;

      case "initials":
      case "Initials":
        return (
          <input
            type="text"
            placeholder={field.placeholder || "N.S"}
            value={field.value}
            className="field-input"
            id={`field-${field.id}`}
            name={`field-${field.id}`}
            style={fontStyles}
            maxLength={getCharacterLimit()}
            disabled
            readOnly
          />
        );

      case "signature":
      case "Signature":
        return (() => {
          try {
            // Check if field.value is a JSON string with signature data
            const signatureData =
              typeof field.value === "string"
                ? JSON.parse(field.value)
                : field.value;

            // Only show signature image if signerType is "Mine"
            const signerType = signatureData?.signerType || "";
            if (signerType === "Mine") {
              // Check if field.value is a direct image URL (from API)
              if (field.value && field.value.startsWith("data:image/")) {
                return (
                  <div className="signature-display">
                    <img
                      src={field.value}
                      alt="Signature"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "60px",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                );
              }

              // Check if there's a valid signature image
              if (
                signatureData &&
                signatureData.signature &&
                signatureData.signature.startsWith("data:image/")
              ) {
                return (
                  <div className="signature-display">
                    <img
                      src={signatureData.signature}
                      alt="Signature"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "60px",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                );
              }
            }

            // If no valid signature is found or signerType is not "Mine", show only the "(Signature)" text
            return <span>(Signature)</span>;
          } catch (e) {
            // If parsing fails, show only the "(Signature)" text
            return <span>(Signature)</span>;
          }
        })();

      default:
        // Fallback for debugging - show the field type
        return (
          <div
            style={{
              padding: "4px",
              fontSize: "10px",
              color: "#999",
              border: "1px dashed #ccc",
              textAlign: "center",
            }}
          >
            {field.type || field.inputType || "Unknown"}
          </div>
        );
    }
  };

  const getContainerClassName = () => {
    const fieldType = normalizeFieldType(field.type || field.inputType);

    switch (fieldType) {
      case "text":
        return "field-input-container field-container";
      case "textarea":
        return "field-textarea-container field-container";
      case "date":
        return "field-date-container field-container";
      case "select":
        return "field-select-container field-container";
      case "checkbox":
        return "field-checkbox-container field-container";
      case "radio":
        return "field-radio-container field-container";
      case "initials":
        return "field-initials-container field-container";
      case "signature":
        return "field-signature field-container";
      default:
        return "field-container";
    }
  };

  const getContainerStyle = () => {
    const fieldType = normalizeFieldType(field.type || field.inputType);
    return fieldType === "signature" ? { cursor: "default" } : {};
  };

  return (
    <div
      className={getContainerClassName()}
      onMouseDown={(e) => e.stopPropagation()}
      style={getContainerStyle()}
    >
      {renderFieldContent()}
    </div>
  );
};

export default PreviewFieldRenderer;
